/**
 * EraseMyData — Public, in-app self-service GDPR right-to-erasure for the
 * anonymous lite-assessment data linked to this browser's `device_id`.
 *
 * What it does NOT cover: explicit lead submissions (assessment_leads,
 * portfolio_leads) where the visitor consented and gave name + email.
 * Those follow our published retention policy — visitors must email
 * info@ekselens.com to action erasure on those.
 */
import React, { useState } from "react";
import { Trash2, ShieldAlert, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { getDeviceId, clearDeviceId } from "../lib/deviceId";
import { API_BASE } from "../lib/api";

export default function EraseMyData() {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const erase = async () => {
    const did = getDeviceId();
    if (!did) {
      toast.error("No anonymous identifier found in this browser");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.delete(
        `${API_BASE}/public/maturity/me`,
        { params: { device_id: did } },
      );
      clearDeviceId();
      try { localStorage.removeItem("ekselens.portfolio.snapshots"); } catch { /* no-op */ }
      try { localStorage.removeItem("ekselens.portfolio.dismissed"); } catch { /* no-op */ }
      setResult(data);
      toast.success(`Erased ${data.drafts_deleted} drafts and ${data.completions_deleted} completions`);
      setConfirming(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Erasure failed");
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div className="border border-emerald-200 bg-emerald-50 p-5" data-testid="erase-done">
        <div className="flex items-start gap-3">
          <CheckCircle2 size={20} className="text-emerald-700 shrink-0 mt-0.5"/>
          <div className="flex-1">
            <div className="font-medium text-emerald-900">Your anonymous data has been erased.</div>
            <p className="text-[14px] text-emerald-800 mt-1">
              We deleted <span className="font-semibold">{result.drafts_deleted}</span> in-progress draft{result.drafts_deleted === 1 ? "" : "s"} and{" "}
              <span className="font-semibold">{result.completions_deleted}</span> completion{result.completions_deleted === 1 ? "" : "s"}{" "}
              tied to your previous browser identifier. A new anonymous identifier will be generated only if you start a new assessment.
            </p>
            <p className="text-[12.5px] text-emerald-700 mt-2">
              To erase any submissions made with your name and email, please contact{" "}
              <a className="underline" href="mailto:info@ekselens.com">info@ekselens.com</a>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-zinc-200 bg-zinc-50 p-5" data-testid="erase-widget">
      <div className="flex items-start gap-3">
        <ShieldAlert size={20} className="text-[#7737F6] shrink-0 mt-0.5"/>
        <div className="flex-1">
          <div className="font-medium text-zinc-900">Erase my anonymous data from this browser</div>
          <p className="text-[14px] text-zinc-700 mt-1">
            Removes every anonymous lite-assessment draft and completed score tied to this browser&apos;s opaque identifier.
            Does <span className="font-semibold">not</span> remove submissions where you provided your name and email —
            those follow our retention policy and can be erased by emailing{" "}
            <a className="underline" href="mailto:info@ekselens.com">info@ekselens.com</a>.
          </p>
          {!confirming ? (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="mt-3 inline-flex items-center gap-2 text-[13px] font-medium border border-zinc-300 bg-white hover:bg-zinc-100 px-3 py-1.5"
              data-testid="erase-trigger"
            >
              <Trash2 size={13}/> Erase my anonymous data
            </button>
          ) : (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-[13px] text-zinc-800">Are you sure? This cannot be undone.</span>
              <button
                type="button"
                onClick={erase}
                disabled={busy}
                className="inline-flex items-center gap-1 text-[13px] font-medium bg-[#7737F6] hover:bg-[#5D28C7] disabled:opacity-60 text-white px-3 py-1.5"
                data-testid="erase-confirm"
              >
                <Trash2 size={13}/> {busy ? "Erasing…" : "Yes, erase"}
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={busy}
                className="text-[13px] text-zinc-600 hover:text-zinc-900 px-3 py-1.5"
                data-testid="erase-cancel"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
